/**
 * React context that holds the current session principal.
 *
 * Provides a single source of truth for who the user is and a small
 * action surface (login, logout, refresh). The provider hits
 * /v1/auth/me on mount and re-resolves after every login/logout.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { authApi, type MeResponse } from "./api";

interface AuthState {
  loading: boolean;
  principal: MeResponse | null;
}

interface AuthContextValue extends AuthState {
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    loading: true,
    principal: null,
  });

  const refresh = useCallback(async () => {
    try {
      const principal = await authApi.me();
      setState({ loading: false, principal });
    } catch {
      // 401 or network failure → no principal. We do not
      // surface the error here; the route gate decides what
      // to do with an unauthenticated state.
      setState({ loading: false, principal: null });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Logout is best-effort; even on a transport failure we
      // clear local state so the SPA returns to the login page.
    }
    setState({ loading: false, principal: null });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, refresh, logout }),
    [state, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error("useAuth must be used inside an <AuthProvider>");
  }
  return value;
}
